import React, { useMemo } from 'react';
import { EchartsWithTheme, Empty, useTheme, Tooltip } from '@maxtropy/components';
import { Spin } from 'antd';
import { keepTwoOrLine, keepZeroOrLine } from '@/utils/utils';
import { getPieChartOptionWithSpin, PieLegendItem } from './chart';
import styles from './index.module.scss';

interface PieCardProps {
  title: string;
  data: PieLegendItem[];
  loading?: boolean;
  unit?: string;
  /** 饼图中间下方显示的标签文字 */
  centerLabel?: string;
  /** 数值是否为整数（不保留小数），默认 false */
  integerValue?: boolean;
}

const COLORS = ['#FF4D4F', '#FFCB47', '#2D8DFF', '#16DD8E', '#ABD335'];

// 字符宽度估算（中文字符约14px，英文/数字约8px）
const estimateTextWidth = (text: string, fontSize: number = 12): number => {
  let width = 0;
  for (const char of text) {
    // 中文字符宽度约为字体大小
    if (/[一-龥]/.test(char)) {
      width += fontSize;
    } else {
      // 英文/数字宽度约为字体大小的一半
      width += fontSize * 0.6;
    }
  }
  return width;
};

const PieCard: React.FC<PieCardProps> = ({
  title,
  data,
  loading = false,
  unit = '',
  centerLabel,
  integerValue = false,
}) => {
  const theme = useTheme();

  // 根据 integerValue 选择格式化函数
  const formatValue = integerValue ? keepZeroOrLine : keepTwoOrLine;

  const pieLegendData: PieLegendItem[] = useMemo(() => {
    return (data ?? []).map((item, index) => ({
      name: item.name,
      value: item.value,
      rate: item.rate,
      color: COLORS[index % COLORS.length],
    }));
  }, [data]);

  // 计算各字段的最大宽度（基于最长字符宽度 + 16px）
  const legendWidths = useMemo(() => {
    // 固定最大宽度限制
    const maxWidthLimits = { name: 100, value: 120, rate: 60 };

    if (!pieLegendData || pieLegendData.length === 0) {
      return {
        name: { width: 60, maxWidth: maxWidthLimits.name },
        value: { width: 80, maxWidth: maxWidthLimits.value },
        rate: { width: 50, maxWidth: maxWidthLimits.rate },
      };
    }

    // 计算每个字段的最大文本宽度
    const maxNameWidth = Math.max(...pieLegendData.map(item => estimateTextWidth(item.name ?? '--')));
    const maxValueWidth = Math.max(
      ...pieLegendData.map(item => estimateTextWidth(`${formatValue(item.value)}${unit}`))
    );
    const maxRateWidth = Math.max(...pieLegendData.map(item => estimateTextWidth(`${keepTwoOrLine(item.rate)}%`)));

    // width: 最长字符宽度 + 24px
    return {
      name: { width: maxNameWidth + 24, maxWidth: maxWidthLimits.name },
      value: { width: maxValueWidth + 24, maxWidth: maxWidthLimits.value },
      rate: { width: maxRateWidth + 24, maxWidth: maxWidthLimits.rate },
    };
  }, [pieLegendData, unit]);

  const chartOption = useMemo(() => {
    return getPieChartOptionWithSpin(pieLegendData, unit, centerLabel, integerValue);
  }, [pieLegendData, unit, centerLabel, integerValue]);

  const isEmpty = !pieLegendData || pieLegendData.length === 0;

  return (
    <div className={styles.pieCard}>
      <div className={styles.header}>
        <span className={styles.label}>{title}</span>
      </div>
      {isEmpty ? (
        <div className={styles.emptyContent}>
          <Empty />
        </div>
      ) : (
        <Spin spinning={loading}>
          <div className={styles.content}>
            <div className={styles.chartWrapper}>
              <EchartsWithTheme
                loading={loading}
                type="pie"
                theme={theme}
                style={{ height: 180 }}
                option={chartOption}
              />
            </div>
            <div className={styles.customerLegend}>
              {(pieLegendData ?? []).map(item => (
                <div className={styles.legendItem} key={item.name}>
                  <div className={styles.icon} style={{ backgroundColor: item.color }} />
                  <Tooltip title={item.name}>
                    <div
                      className={styles.legendName}
                      style={{ width: legendWidths.name.width, maxWidth: legendWidths.name.maxWidth }}
                    >
                      {item.name ?? '--'}
                    </div>
                  </Tooltip>
                  <Tooltip title={`${formatValue(item.value)}${unit}`}>
                    <div
                      className={styles.legendValue}
                      style={{ width: legendWidths.value.width, maxWidth: legendWidths.value.maxWidth }}
                    >
                      {formatValue(item.value)}
                      {unit}
                    </div>
                  </Tooltip>
                  <Tooltip title={`${keepTwoOrLine(item.rate)}%`}>
                    <div
                      className={styles.legendRate}
                      style={{ width: legendWidths.rate.width, maxWidth: legendWidths.rate.maxWidth }}
                    >
                      {keepTwoOrLine(item.rate)}%
                    </div>
                  </Tooltip>
                </div>
              ))}
            </div>
          </div>
        </Spin>
      )}
    </div>
  );
};

export default PieCard;
